import {CalendarIcon, ChevronLeft, ChevronRight, X} from "lucide-react";
import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";

import {Button} from "@/components/ui/button";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {cn} from "@/lib/utils";
import {
  buildPlateExpiresOn,
  formatPlateExpiresOn,
  parsePlateExpiresOnParts,
} from "@/src/modules/citations/lib/plate-expires-on";
import {parseAppLocale} from "@/src/modules/core/i18n";
import {getIntlLocaleTag} from "@/src/modules/core/i18n/day-picker-locale";

const MONTH_VALUES = Array.from({length: 12}, (_, index) =>
  String(index + 1).padStart(2, "0"),
);

type MonthYearPickerProps = {
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  className?: string;
  pickerDisabled?: boolean;
  minYear?: number;
  maxYear?: number;
};

export default function MonthYearPicker({
  value,
  onChange,
  placeholder = "",
  className = "",
  pickerDisabled = false,
  minYear,
  maxYear,
}: MonthYearPickerProps) {
  const {i18n} = useTranslation();
  const locale = parseAppLocale(i18n.language);
  const intlLocaleTag = getIntlLocaleTag(locale);
  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());

  const currentYear = new Date().getFullYear();
  const resolvedMinYear = minYear ?? currentYear - 30;
  const resolvedMaxYear = maxYear ?? currentYear + 15;

  const {year: selectedYear, month: selectedMonth} = parsePlateExpiresOnParts(value);
  const displayLabel = value ? formatPlateExpiresOn(value, locale) : null;

  const monthOptions = useMemo(
    () =>
      MONTH_VALUES.map((monthValue) => {
        const monthNum = Number.parseInt(monthValue, 10);
        const shortLabel = new Date(2000, monthNum - 1, 1).toLocaleString(intlLocaleTag, {
          month: "short",
        });
        return {label: shortLabel, value: monthValue};
      }),
    [intlLocaleTag],
  );

  useEffect(() => {
    if (!isOpen) return;
    const parsedYear = selectedYear ? Number.parseInt(selectedYear, 10) : currentYear;
    setViewYear(parsedYear);
  }, [isOpen, selectedYear, currentYear]);

  const canGoPrevYear = viewYear > resolvedMinYear;
  const canGoNextYear = viewYear < resolvedMaxYear;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <div className="relative">
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="outline"
              className={cn(
                "w-full justify-start border-slate-300 text-left font-normal",
                className,
                !displayLabel && "text-slate-500",
              )}
              disabled={pickerDisabled}
              onClick={() => !pickerDisabled && setIsOpen(!isOpen)}
            />
          }
        >
          {displayLabel ?? placeholder}
        </PopoverTrigger>
        <div className="absolute top-1/2 right-1 -translate-y-1/2">
          {displayLabel ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange(null);
                setIsOpen(false);
              }}
            >
              <X className="size-4 text-slate-500" />
              <span className="sr-only">Clear</span>
            </Button>
          ) : (
            <div className="px-3">
              <CalendarIcon className="size-4 text-slate-500" />
            </div>
          )}
        </div>
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <div
          className={cn(
            "bg-background p-3 [--cell-size:--spacing(8)]",
            "[[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
          )}
          data-slot="calendar"
        >
          <div className="mb-2 flex h-(--cell-size) items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-(--cell-size) p-0"
              disabled={!canGoPrevYear}
              onClick={() => setViewYear((year) => year - 1)}
            >
              <ChevronLeft className="size-4" />
              <span className="sr-only">Previous year</span>
            </Button>
            <span className="text-sm font-medium tabular-nums">{viewYear}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-(--cell-size) p-0"
              disabled={!canGoNextYear}
              onClick={() => setViewYear((year) => year + 1)}
            >
              <ChevronRight className="size-4" />
              <span className="sr-only">Next year</span>
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-1">
            {monthOptions.map((month) => {
              const isSelected =
                selectedYear === String(viewYear) && selectedMonth === month.value;

              return (
                <Button
                  key={month.value}
                  type="button"
                  variant="ghost"
                  className={cn(
                    "h-(--cell-size) w-full min-w-(--cell-size) font-normal",
                    isSelected &&
                      "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                  )}
                  onClick={() => {
                    onChange(buildPlateExpiresOn(String(viewYear), month.value));
                    setIsOpen(false);
                  }}
                >
                  {month.label}
                </Button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
